// oxlint-disable complexity, max-statements
import type { QueryClient } from '@tanstack/react-query';
import { merge } from 'lodash-es';

import { localStorage } from '@change-corgi/core/storage';

import { treatExperiment } from '#src/app/shared/api/experiments';
import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';
import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from '#src/app/shared/api/petitionDraft';
import type { UtilityContextWithTracker } from '#src/app/shared/contexts/asyncData';

import type { StartAPetitionDataQuery } from '../api.graphql';

import { getViewerDeviceId } from './utils';

const STARFISH_CHAT_AGENT_EXPERIMENT_NAME = 'starfish_sap_chat_agent';

type Experiments = StartAPetitionDataQuery['experiments'];
/**
 * Resolves the persisted-flow variant for the current viewer from the experiments payload,
 * treating (recording exposure for) whichever SAP flow experiment is currently active.
 *
 * starfish_sap_flow_experiment is a generic, dynamically-named config shared across SAP flow
 * experiments, so its variation values are only meaningful while its resolved experimentName
 * matches one this page cares about. It's queried with treat:false, and only treated here
 * once we've confirmed the resolved experimentName is one this page renders — otherwise
 * we'd record an exposure for whatever unrelated experiment currently occupies this shared slot.
 *
 * TODO: once more SAP flow experiments use this generic config, migrate to a consistent
 * `<experiment_name>_<variant>` persisted-variant naming scheme.
 */
async function getVariant(experiments: Experiments, utilityContext: UtilityContextWithTracker) {
	const { experimentName, variation } = experiments.starfishSapFlowExperiment;

	if (experimentName === STARFISH_CHAT_AGENT_EXPERIMENT_NAME) {
		await treatExperiment(utilityContext, experimentName);

		if (variation === 'variant_a') return 'chat_agent' as const;
		if (variation === 'fake_control') return 'fake_control' as const;

		return 'control' as const;
	}

	return 'control' as const;
}

/**
 * Normalizes a variant name for comparison purposes, mapping `fake_control` to `control`.
 *
 * fake_control maps to the same downstream flow as control, so treat them as equivalent when
 * comparing the persisted draft variant to the latest assignment — otherwise users reassigned
 * between control and fake_control would have their in-progress drafts reset unnecessarily.
 */
function normalizeVariant(variant: string) {
	return variant === 'fake_control' ? 'control' : variant;
}

type FindOrCreateDraftParams = StartAPetitionDataQuery & { draftAuthorId: string };
type FindOrCreateDraftOptions = UtilityContextWithTracker & { queryClient: QueryClient };

export async function findOrCreateDraft(
	{ draftAuthorId, experiments, viewer }: FindOrCreateDraftParams,
	options: FindOrCreateDraftOptions,
): Promise<PetitionDraft> {
	try {
		const draftClient = new DraftClient([
			new LocalStorageAdapter(localStorage),
			new InMemoryAdapter(options.queryClient),
		]);

		const currentDraft = await draftClient.getDraft(draftAuthorId);
		const variant = await getVariant(experiments, options);
		const currentLoginState = viewer.loginState;
		const deviceId = await getViewerDeviceId(options.userAgent);

		const defaultMetadata = {
			variant,
			user: { id: viewer.user?.id, initialLoginState: currentLoginState },
			deviceId,
		} as const;

		if (!currentDraft) {
			return await draftClient.createDraft(draftAuthorId, { metadata: defaultMetadata });
		}

		// Only reset on a genuine account switch: a stored id AND a concrete *new* id that differ.
		// A transition to a null/guest viewer must NOT reset — that would wipe an in-progress draft over a transient loss
		// of identity. lodash merge skips the undefined id below, so the stored id is preserved and the
		// draft re-attaches cleanly when the session re-resolves to the same user.
		const didUserChange =
			currentDraft.metadata?.user?.id && viewer.user?.id && viewer.user.id !== currentDraft.metadata.user.id;
		if (didUserChange) {
			void options.track('draft_reconciled', {
				reason: 'user_change',
				did_reset: true,
				previous_user_id: currentDraft.metadata?.user?.id,
				new_user_id: viewer.user.id,
				previous_login_state: currentDraft.metadata?.user?.initialLoginState,
				current_login_state: currentLoginState,
			});
			return await draftClient.createDraft(draftAuthorId, { metadata: defaultMetadata });
		}

		// We have a stored owner but the viewer resolved without a user id (a guest/identified-session lapse
		// or a token-refresh blip). We intentionally do NOT reset — the draft is preserved and its stored owner
		// id survives the merge below. Tracked so we keep visibility into this churn even though it no longer
		// resets: this transition used to be the dominant (false-positive) source of user resets.
		const didIdentityBecomeUnstable = currentDraft.metadata?.user?.id && !viewer.user?.id;
		if (didIdentityBecomeUnstable) {
			void options.track('draft_reconciled', {
				reason: 'identity_unstable',
				did_reset: false,
				previous_user_id: currentDraft.metadata?.user?.id,
				new_user_id: null,
				previous_login_state: currentDraft.metadata?.user?.initialLoginState,
				current_login_state: currentLoginState,
			});
		}

		// track variant changes
		const didVariantChange =
			currentDraft.metadata?.variant && normalizeVariant(currentDraft.metadata.variant) !== normalizeVariant(variant);
		if (didVariantChange) {
			void options.track('draft_reconciled', {
				reason: 'variant_change',
				did_reset: false,
				previous_variant: currentDraft.metadata?.variant,
				new_variant: variant,
			});
		}

		// track device changes
		const didDeviceChange = currentDraft.metadata?.deviceId && deviceId !== currentDraft.metadata.deviceId;
		if (didDeviceChange) {
			void options.track('draft_reconciled', { reason: 'device_change', did_reset: false });
		}

		// always ensure metadata has the latest user id/device ids/variant
		const metadata = merge({}, currentDraft.metadata, {
			user: { id: viewer.user?.id },
			deviceId,
			variant: currentDraft.metadata?.variant ?? variant,
		});

		return { ...currentDraft, metadata };
	} catch (error) {
		// report and throw
		throw new Error(`Failed to find or create draft: ${(error as Error).message}`, { cause: error });
	}
}
